import { Uploader } from '@wix/media-manager-sdk';
import { always, cond, evolve, groupBy, isEmpty, last, map, path, pathOr, pipe, prop, propOr, T } from 'ramda';
import { isFunction, mergeLeft } from 'ramda-adjunct';
import { Acl } from '../../../constants/Acl';
import { ExternalSource, MainSource } from '../../../constants/Source';
import { createFileOriginTag } from '../../../constants/SystemTags';
import { getLocale } from '../../env/getLocale';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { useRootFolder } from '../../hoc/withRootFolder';
import { MediaManagerSDKFile, MediaManagerSDKFolder, MediaManagerSDKUploadingFile } from '../../media-manager-sdk';
import {
  canItemBeImportedByCopying,
  canItemBeImportedByImportFn,
  getWixSiteMediaTokenFromFolderId,
  isNativeFile,
} from '../../media-manager-sdk/items';
import { isTranscodableMediaType } from '../../media-manager-sdk/transcoding';
import { isVideoMediaType } from '../../mediaType';
import { parseSiteMediaToken } from '../../siteMediaToken';
import { defineMediaType, defineRealFileMediaType, getSecureMediaType } from '../../uploadingFile';
import { useTrackUpload } from '../bi/useTrackUpload';
import { useAdapter, useGetAdapter } from '../useAdapter';
import { useImportOperations } from './useFilesUploader';
import { FileOrigin, TEncodingOptions, TUseUploadFilesHandlers, TUseUploadFilesHandlersHandlers } from './types';
import { isFont } from './utils';
import { MediaType } from '../../../constants/MediaType';
import { MINIMUM_FILE_SIZE_TO_BE_RESUMABLE_UPLOAD } from './constants';
import { isSiteFilesFolderInBoards } from '../../media-manager-sdk/boards';

const useGetAsyncImportFnAndAdapter = () => {
  const getItemAdapter = useGetAdapter();
  const { importOperations } = useImportOperations();

  const getAsyncImportFnAndAdapter = (file: MediaManagerSDKFile) => {
    const itemAdapter = getItemAdapter(file.source);
    const importAsyncFn = pathOr(null, ['item', 'importAsync'], itemAdapter);

    if (!importAsyncFn) {
      return [importAsyncFn, itemAdapter];
    }

    const wrappedAsyncFn = (...args) =>
      new Promise((resolve, reject) => {
        itemAdapter.item.importAsync(...args).then((response) => {
          importOperations.set(response.data.id, {
            onDone: (updatedItemData) => resolve(evolve({ data: mergeLeft(updatedItemData) }, response)),
            onFail: (updatedItemData) => reject(evolve({ data: mergeLeft(updatedItemData) }, response)),
          });
        }, reject);
      });

    return [wrappedAsyncFn, itemAdapter];
  };

  return getAsyncImportFnAndAdapter;
};

const prefailedUploadFn =
  (reason: string, args = []) =>
  () => {
    const err = Object.assign(new Error(reason), { data: { error_info: { key: reason, args } } });
    return Promise.reject(err);
  };

export const useUploadFilesHandlers: TUseUploadFilesHandlers = ({ source, folderPath }) => {
  const { mmgrSettings } = useMMGRSettings();
  const {
    isSecureContext,
    namespace,
    vision,
    videoEncodingProfile,
    useFastTranscoding,
    siteMediaToken,
    isPreventFontsUploadExperimentEnabled,
    forceUploadErrorsExperimentValue,
  } = mmgrSettings;

  const lastFolderId = folderPath ? propOr('', 'id', last(folderPath)) : '';
  const uploadToBoard = lastFolderId && isSiteFilesFolderInBoards(folderPath[0].source, lastFolderId);
  // [0] is 'boards' root, [1] will be the board itself, [2] will be the folder in the board
  // if upload to board, take token from /boards/ref response
  const firstFolderInBoard = uploadToBoard && folderPath[2];
  const uploadToken = firstFolderInBoard ? firstFolderInBoard.siteMediaToken : siteMediaToken;

  const privateSiteAdapter = useAdapter(MainSource.SiteFiles, uploadToken);
  const wixVideoAdapter = useAdapter(ExternalSource.WixVideo);

  const uploadToSiteFilesFolderPath = [useRootFolder(MainSource.SiteFiles)];
  const getLastFolderSource = pipe<MediaManagerSDKFolder[], MediaManagerSDKFolder, string>(last, prop('source'));

  const isUploadToSiteFiles =
    source === MainSource.SiteFiles || getLastFolderSource(folderPath) === MainSource.SiteFiles;

  const destinationPath = isUploadToSiteFiles ? folderPath : uploadToSiteFilesFolderPath;

  const acl = isSecureContext ? Acl.Private : Acl.Public;

  const uploadFn = privateSiteAdapter.item.upload;
  const uploadByUrlFn = privateSiteAdapter.item.uploadByUrl;

  const getAsyncImportFnAndAdapter = useGetAsyncImportFnAndAdapter();

  const generateEncodingOptions = (mediaType) => {
    const encodingOptions: TEncodingOptions = {};

    if (isVideoMediaType(mediaType) && videoEncodingProfile) {
      encodingOptions.profile_name = videoEncodingProfile;
    }

    if (isTranscodableMediaType(mediaType) && useFastTranscoding) {
      encodingOptions.fast_transcode = useFastTranscoding;
    }

    return encodingOptions;
  };

  const { importOperations } = useImportOperations();

  const { trackUpload } = useTrackUpload();

  const uploadFiles: TUseUploadFilesHandlersHandlers['uploadFiles'] = (
    files,
    uploadFolderPath = null,
    options = {},
  ) => {
    const filesArray = Array.from(files);
    const folderPath = Array.isArray(uploadFolderPath) ? uploadFolderPath : uploadFolderPath && [uploadFolderPath];

    const { id: folderId, name: folderName } = last(folderPath || destinationPath);

    function isFileBigEnoughForTus(fileSize) {
      return fileSize > MINIMUM_FILE_SIZE_TO_BE_RESUMABLE_UPLOAD;
    }

    const shouldFileBeResumable = (file) => {
      return isFileBigEnoughForTus(file.size);
    };

    const fileSources = map((file) => {
      const toUploadMediaType = defineMediaType(file.type, isSecureContext, namespace);
      const realFileMediaType = defineRealFileMediaType(file.type);
      const encodingOptions = generateEncodingOptions(realFileMediaType);

      const isAttemptToUploadFonts = isFont(file.name);

      const overrideFileSource =
        isAttemptToUploadFonts && isPreventFontsUploadExperimentEnabled
          ? {
              uploadFn: prefailedUploadFn('wmp_error.fonts_not_allowed'),
            }
          : forceUploadErrorsExperimentValue && forceUploadErrorsExperimentValue !== 'false'
          ? { uploadFn: prefailedUploadFn(forceUploadErrorsExperimentValue, ['_PLACEHOLDER_', '999Kb']) }
          : {};

      return {
        source: {
          file,
          // @ts-expect-error
          folderId: file.mmgrFolderId || folderId,
          mediaType: toUploadMediaType,
          acl,
          encodingOptions,
          tags: [createFileOriginTag(FileOrigin.Uploaded), ...(options.tags || [])],
          language: getLocale(),
        },
        uploadFn,
        adapter: privateSiteAdapter,
        options: {
          isTus: shouldFileBeResumable(file),
          vision,
          folderName,
          realFileMediaType,
          ...(path(['bi', 'files'], options) ? { bi: options.bi.files.get(file) } : {}),
        },
        ...overrideFileSource,
      };
    }, filesArray);

    const mappedFiles: MediaManagerSDKUploadingFile[] = Uploader.upload(fileSources);
    trackUpload(mappedFiles);
  };

  const wixSiteAdapter = useAdapter(ExternalSource.WixSite);
  const tokenData = parseSiteMediaToken(siteMediaToken);

  const importToCurrentSite: TUseUploadFilesHandlersHandlers['importToCurrentSite'] = (
    files,
    uploadFolderPath,
    options = {},
  ) => {
    const { id: folderId, name: folderName } = last(uploadFolderPath || destinationPath);

    const fileSources = map(
      (file) => ({
        source: {
          file,
          name: file.name,
          url: file.fileUrl,
          folderId,
          mediaType: isSecureContext ? getSecureMediaType(file.mediaType, namespace) : file.mediaType,
          tags: [],
          acl,
          language: getLocale(),
        },
        uploadFn: (source) => {
          const destination = {
            siteId: tokenData.metaSiteId,
            id: folderId,
          };
          const options = {
            userId: tokenData.userId,
          };

          return new Promise((resolve) => {
            wixSiteAdapter.items.importToSite(source.file, destination, options).then((response) => {
              const { opId } = response.data;

              importOperations.set(opId, {
                onDone: resolve,
              });
            });
          });
        },
        adapter: privateSiteAdapter,
        options: {
          folderName,
          ...(path(['bi', 'files'], options) ? { bi: options.bi.files.get(file) } : {}),
        },
      }),
      files,
    );

    const mappedFiles = Uploader.upload(fileSources);
    trackUpload(mappedFiles);
  };

  const uploadFilesByUrl: TUseUploadFilesHandlersHandlers['uploadFilesByUrl'] = (
    files,
    uploadFolderPath,
    options = {},
  ) => {
    const { id: folderId, name: folderName } = last(uploadFolderPath || destinationPath);

    const fileSources = map((file) => {
      let { mediaType, source, name, fileUrl, tags } = file;

      mediaType = isSecureContext ? getSecureMediaType(mediaType, namespace) : mediaType;
      const encodingOptions = generateEncodingOptions(mediaType);

      const [importAsyncFn, itemAdapter] = getAsyncImportFnAndAdapter(file);
      const canUseAsyncImport = isFunction(importAsyncFn);

      const uploadFn = canUseAsyncImport ? importAsyncFn : uploadByUrlFn;
      const adapter = canUseAsyncImport ? itemAdapter : privateSiteAdapter;

      return {
        source: {
          file,
          name,
          url: fileUrl,
          folderId,
          mediaType,
          tags,
          systemTags: [createFileOriginTag(source || options.source)],
          acl,
          encodingOptions,
          language: getLocale(),
        },
        uploadFn,
        adapter,
        options: {
          vision,
          folderName,
          ...(path(['bi', 'files'], options) ? { bi: options.bi.files.get(file) } : {}),
          bypassMaxParallelUploads: true,
        },
      };
    }, files);

    const mappedFiles = Uploader.upload(fileSources);
    trackUpload(mappedFiles);
  };

  const importFromWixVideo = (files, options = {}) => {
    const fileSources = map(
      (item) => ({
        source: {
          file: item,
          mediaType: MediaType.Video,
        },
        uploadFn: () =>
          wixVideoAdapter.items.importToSite([item], siteMediaToken, {
            src_site_id: prop('site_id', item),
            src_site_token: getWixSiteMediaTokenFromFolderId(prop('folderId', item)),
          }),
        options: {
          // @ts-expect-error
          ...(path(['bi', 'files'], options) ? { bi: options.bi.files.get(item) } : {}),
        },
      }),
      files,
    );
    const mappedFiles = Uploader.upload(fileSources);
    trackUpload(mappedFiles);
  };

  const importFiles: TUseUploadFilesHandlersHandlers['importFiles'] = (files, options = {}) => {
    const {
      itemsToUpload = [],
      itemsToCopy = [],
      itemsToImportByUrl = [],
      itemsToImportFromWixVideo = [],
    } = groupBy(
      cond([
        [isNativeFile, always('itemsToUpload')],
        [canItemBeImportedByCopying, always('itemsToCopy')],
        [canItemBeImportedByImportFn, always('itemsToImportFromWixVideo')],
        [T, always('itemsToImportByUrl')],
      ]),
      Array.from(files),
    );

    if (!isEmpty(itemsToUpload)) {
      uploadFiles(itemsToUpload as unknown as File[], null, options);
    }

    if (!isEmpty(itemsToCopy)) {
      importToCurrentSite(itemsToCopy as MediaManagerSDKFile[], null, options);
    }

    if (!isEmpty(itemsToImportByUrl)) {
      uploadFilesByUrl(itemsToImportByUrl as MediaManagerSDKFile[], null, { ...options, source: ExternalSource.Link });
    }

    if (!isEmpty(itemsToImportFromWixVideo)) {
      importFromWixVideo(itemsToImportFromWixVideo, options);
    }
  };

  return { uploadFiles, uploadFilesByUrl, importToCurrentSite, importFiles };
};
